import ESigningFormApi from '../ESigningForm.api';
import ESigningFormPremiumAgreementValidation from './ESigningFormPremiumAgreementValidation';

/**
 * Step-1 logic for PremiumAgreement form variant.
 * Keeps Premium-specific dropdown behavior isolated from other agreement forms.
 *
 * @param {*} base - ESigningFormBase instance
 * @param {string} [suffix] - Appended to every element id this class looks up, so two instances
 * can coexist on the same page (combined ClimateAndNaturePlus form). Defaults to '' (single variant).
 * @param {object} [options]
 * @param {boolean} [options.isStandalone] - When false, this instance does not own the shared
 * Step1Next button or submit flow - an orchestrator (e.g. ESigningFormCombinedPremiumAgreement)
 * does that instead and only uses this instance for its own section's dropdown/validation logic.
 * @param {boolean} [options.managesDeliveryPeriodList] - When false, skips fetching/populating the
 * (shared) delivery period dropdown - another instance on the page already owns that.
 */
export default class ESigningFormPremiumAgreement {
    constructor(base, suffix = '', {
        isStandalone = true,
        managesDeliveryPeriodList = true,
        itemsFormType = null,
        onStateChange = null,
    } = {}) {
        this.base = base;
        this.suffix = suffix;
        this.isStandalone = isStandalone;
        this.managesDeliveryPeriodList = managesDeliveryPeriodList;
        this.itemsFormType = itemsFormType;
        this.onStateChange = onStateChange;
        if (this.isStandalone) {
            this.base.submitEndpoint = '/api/e-signing/v2/premium/initiate-signing';
        }
        this.step1Validation = new ESigningFormPremiumAgreementValidation(suffix);
        this.itemDropdownList = document.getElementById('premiumProductItemList' + suffix);
        this.deliveryPeriodDropdownList = document.getElementById('premiumDeliveryPeriodList');
        this.itemInput = document.getElementById('premiumProductItem' + suffix);
        this.deliveryPeriodInput = document.getElementById('premiumDeliveryPeriod');
        this.itemShowcase = document.querySelector('#premiumProductItemDropdown' + suffix + ' .showcase > a');
        this.deliveryPeriodShowcase = document.querySelector('#premiumDeliveryPeriodDropdown .showcase > a');
        this.itemContainer = document.querySelector('.premium-product-item[data-product-suffix="' + suffix + '"]');
        this.itemDropdown = document.getElementById('premiumProductItemDropdown' + suffix);
        this.deliveryPeriodDropdown = document.getElementById('premiumDeliveryPeriodDropdown');
        this.signingTemplateInput = document.getElementById('signingTemplate');
        this.formType = document.getElementById('formType');
        this.customerNumberInput = document.getElementById('customerNumber');
        this.latestAgreementExistsRequestId = 0;

        this.init();
    }

    async init() {
        if (!this.itemDropdownList || !this.itemInput || !this.deliveryPeriodDropdownList || !this.deliveryPeriodInput) {
            return;
        }

        if (this.managesDeliveryPeriodList) {
            await this.loadDeliveryPeriodsAsync();
        }
        this.bindEvents();
        this.initStep1EventListeners();
        this.updateStep1NextButtonState();
        this.bindClimateNatureMeasureListeners();

        this.setupDropdownToggle(this.itemDropdown);
        if (this.managesDeliveryPeriodList) {
            this.setupDropdownToggle(this.deliveryPeriodDropdown);
        }
    }

    bindEvents() {
        this.itemDropdownList.addEventListener('click', async (evt) => {
            const option = evt.target.closest('li[data-value]');
            if (!option) {
                return;
            }

            evt.preventDefault();
            await this.handleItemSelection(option);
        });

        if (this.deliveryPeriodDropdownList) {
            this.deliveryPeriodDropdownList.addEventListener('click', (evt) => {
                const option = evt.target.closest('li[data-value]');
                if (!option) {
                    return;
                }

                evt.preventDefault();
                this.handleDeliveryPeriodSelection(option);
            });
        }
    }

    initStep1EventListeners() {
        if (this.isStandalone) {
            const step1NextBtn = document.querySelector('#Step1Next');
            if (step1NextBtn) {
                step1NextBtn.addEventListener('click', this.handleStep1Next);
            }
        }

        this.getRequiredStep1Fields().forEach((field) => {
            if (!field) return;

            field.addEventListener('input', () => this.updateStep1NextButtonState());
            field.addEventListener('change', () => this.updateStep1NextButtonState());
            field.addEventListener('blur', () => this.updateStep1NextButtonState());
        });

        this.bindStep1FieldValidationListeners(this.step1Validation.getContractQuantityField(), {
            input: false,
            change: true,
            blur: true,
        });
    }

    bindStep1FieldValidationListeners(field, validationTriggerMap) {
        if (!field || !validationTriggerMap) {
            return;
        }

        Object.entries(validationTriggerMap).forEach(([eventName, showError]) => {
            field.addEventListener(eventName, () => this.step1Validation.validateStep1QuantityField(showError));
        });
    }

    bindClimateNatureMeasureListeners() {
        const hiddenSummaryField = document.getElementById('climateNatureMeasures' + this.suffix);
        if (!hiddenSummaryField) {
            return;
        }

        const regularCheckboxes = this.step1Validation.getClimateNatureBiodiversityCheckboxes();
        const deferredCheckbox = this.step1Validation.getClimateNatureDeferredCheckbox();
        const allCheckboxes = [...regularCheckboxes, deferredCheckbox].filter(Boolean);

        const updateSummaryAndValidate = (changedCheckbox) => {
            if (deferredCheckbox && changedCheckbox === deferredCheckbox && deferredCheckbox.checked) {
                regularCheckboxes.forEach((checkbox) => {
                    checkbox.checked = false;
                });
            }

            if (deferredCheckbox && changedCheckbox && changedCheckbox !== deferredCheckbox && changedCheckbox.checked) {
                deferredCheckbox.checked = false;
            }

            const selectedRegular = regularCheckboxes
                .filter((checkbox) => checkbox.checked)
                .map((checkbox) => checkbox.value);
            const deferredSelected = deferredCheckbox?.checked ? [deferredCheckbox.value] : [];
            const allSelected = [...selectedRegular, ...deferredSelected];

            const selectedLabelTexts = allCheckboxes
                .filter((checkbox) => checkbox.checked)
                .map((checkbox) => {
                    const labelText = checkbox
                        .closest('.lm__element-margin-bottom')
                        ?.querySelector('span')
                        ?.textContent;

                    return (labelText || '').trim();
                })
                .filter((text) => text.length > 0);

            // Store enum names as JSON array for backend model binding
            hiddenSummaryField.value = JSON.stringify(allSelected);
            hiddenSummaryField.dataset.reviewValue = selectedLabelTexts.join(', ');

            this.step1Validation.validateClimateNatureBiodiversitySelection(false);
            this.updateStep1NextButtonState();
        };

        allCheckboxes.forEach((checkbox) => {
            checkbox.addEventListener('change', () => updateSummaryAndValidate(checkbox));
        });

        updateSummaryAndValidate();
    }

    async loadItemsAsync(deliveryPeriodId, signingTemplateId) {
        if (!deliveryPeriodId || !signingTemplateId) {
            this.itemDropdownList.innerHTML = '';
            return;
        }
        const response = await ESigningFormApi.fetchJson('/api/e-signing/agreements/premium/product-items', {
            premiumDeliveryPeriod: deliveryPeriodId,
            formType: this.itemsFormType || this.formType?.value || '',
        });

        if (response.status !== 200 || !Array.isArray(response.body?.items)) {
            this.itemDropdownList.innerHTML = '';
            return;
        }

        this.itemDropdownList.innerHTML = response.body.items
            .map((item) => {
                const id = item.Id ?? item.id ?? '';
                const name = item.Name ?? item.name ?? '';
                const price = item.Price ?? item.price;
                const priceAttr = price === null || price === undefined ? '' : ` data-price="${price}"`;

                return `<li data-value="${id}"${priceAttr}><a href="javascript:void(0)">${name}</a></li>`;
            })
            .join('');

        const singleOption = this.itemDropdownList.querySelector('li[data-value]');
        if (singleOption && this.itemDropdownList.querySelectorAll('li[data-value]').length === 1) {
            await this.handleItemSelection(singleOption);
        }
    }

    async loadDeliveryPeriodsAsync() {
        if (!this.deliveryPeriodDropdownList) {
            return;
        }

        const response = await ESigningFormApi.fetchJson('/api/e-signing/agreements/premium/delivery-periods');
        if (response.status !== 200 || !Array.isArray(response.body?.items)) {
            this.deliveryPeriodDropdownList.innerHTML = '';
            return;
        }

        this.deliveryPeriodDropdownList.innerHTML = response.body.items
            .map((item) => `<li data-value="${item.Id}"><a href="javascript:void(0)">${item.Name}</a></li>`)
            .join('');
    }

    async handleItemSelection(optionElement) {
        const selectedItemId = optionElement.dataset.value || '';
        const selectedItemText = optionElement.textContent?.trim() || '';
        const selectedItemPrice = optionElement.dataset.price || '';

        this.itemInput.value = selectedItemId;
        this.itemInput.dataset.price = selectedItemPrice;
        this.itemInput.dataset.name = selectedItemText;
        if (this.itemShowcase && selectedItemText) {
            this.itemShowcase.textContent = selectedItemText;
        }

        this.hideDropdown('#premiumProductItemDropdown' + this.suffix + ' .dropdown');
        await this.checkAgreementExistsForSelection();
        this.updateStep1NextButtonState();
    }

    async handleDeliveryPeriodSelection(optionElement) {
        if (!this.deliveryPeriodInput) {
            return;
        }

        const selectedDeliveryPeriodId = optionElement.dataset.value || '';
        const selectedDeliveryPeriodText = optionElement.textContent?.trim() || '';

        this.deliveryPeriodInput.value = selectedDeliveryPeriodId;
        if (this.deliveryPeriodShowcase && selectedDeliveryPeriodText) {
            this.deliveryPeriodShowcase.textContent = selectedDeliveryPeriodText;
        }

        this.latestAgreementExistsRequestId += 1;
        this.step1Validation.setPremiumAgreementExistsState({
            exists: false,
            pending: false,
            message: '',
        });
        this.resetItemSelection();

        this.hideDropdown('#premiumDeliveryPeriodDropdown .dropdown');

        await this.refreshItemsForSelections();
        this.updateStep1NextButtonState();
    }

    async refreshItemsForSelections() {
        const deliveryPeriodId = this.deliveryPeriodInput?.value || '';
        const signingTemplateId = this.signingTemplateInput?.value || '';

        if (!deliveryPeriodId || !signingTemplateId) {
            this.setItemContainerEnabled(false);
            this.itemDropdownList.innerHTML = '';
            return;
        }

        await this.loadItemsAsync(deliveryPeriodId, signingTemplateId);

        const hasItems = this.itemDropdownList.children.length > 0;
        this.setItemContainerEnabled(hasItems);
    }

    handleStep1Next = () => {
        if (!this.step1Validation.validateStep1()) {
            return;
        }

        this.base.showStep(2);
    };

    updateStep1NextButtonState() {
        if (!this.isStandalone) {
            // An orchestrator (combined form) owns the shared Step1Next button instead.
            this.onStateChange?.();
            return;
        }

        const nextButton = document.getElementById('Step1Next');
        if (!nextButton) {
            return;
        }

        const hasTemplate = this.signingTemplateInput?.value?.trim() !== '';
        this.base.toggleStep1NextVisibility(hasTemplate);

        if (this.step1Validation.isStep1Valid()) {
            nextButton.classList.remove('disabled');
        } else {
            nextButton.classList.add('disabled');
        }
    }

    getRequiredStep1Fields() {
        return this.step1Validation.getRequiredStep1Fields();
    }

    hideDropdown(selector) {
        const dropdown = document.querySelector(selector);
        if (dropdown) {
            dropdown.style.display = 'none';
        }
    }

    resetItemSelection() {
        this.itemInput.value = '';
        delete this.itemInput.dataset.price;
        if (this.itemShowcase) {
            this.itemShowcase.textContent = this.itemDropdown?.dataset.selectText || 'Select';
        }

        this.step1Validation.setPremiumAgreementExistsState({
            exists: false,
            pending: false,
            message: '',
        });
    }

    async checkAgreementExistsForSelection() {
        const customerNumber = this.customerNumberInput?.value?.trim() || '';
        const itemId = this.itemInput?.value?.trim() || '';
        const harvestYear = this.deliveryPeriodInput?.value?.trim() || '';

        if (!customerNumber || !itemId || !harvestYear) {
            this.step1Validation.setPremiumAgreementExistsState({
                exists: false,
                pending: false,
                message: '',
            });
            return;
        }

        const requestId = ++this.latestAgreementExistsRequestId;
        this.step1Validation.setPremiumAgreementExistsState({
            exists: false,
            pending: true,
            message: '',
        });
        this.updateStep1NextButtonState();

        const response = await ESigningFormApi.fetchJson(
            '/api/e-signing/agreements/premium/agreement-exists',
            {
                customerNumber,
                itemId,
                harvestYear,
            }
        );

        if (requestId !== this.latestAgreementExistsRequestId) {
            return;
        }

        if (response.status === 200 && typeof response.body?.exists === 'boolean') {
            const alreadyExistsMessage =
                eSigningTranslation?.premiumAgreement?.agreementAlreadyExists
                || 'Det finns redan ett avtal för den här sorten och skördeåret';

            this.step1Validation.setPremiumAgreementExistsState({
                exists: response.body.exists,
                pending: false,
                message: alreadyExistsMessage,
            });

            // Render warning immediately when backend confirms duplicate agreement.
            this.step1Validation.validatePremiumAgreementNotExists(true);
            return;
        }

        this.step1Validation.setPremiumAgreementExistsState({
            exists: false,
            pending: false,
            message: '',
        });
    }

    setItemContainerEnabled(isEnabled) {
        if (!this.itemContainer) {
            return;
        }

        if (isEnabled) {
            this.itemContainer.classList.remove('disabled');
            return;
        }

        this.itemContainer.classList.add('disabled');
        this.resetItemSelection();
    }

    setupDropdownToggle(dropdownRoot) {
        if (!dropdownRoot) {
            return;
        }

        const showcaseLink = dropdownRoot.querySelector('.showcase > a');
        if (showcaseLink) {
            showcaseLink.addEventListener('click', (event) => {
                event.preventDefault();
                const dropdown = dropdownRoot.querySelector('.dropdown');
                if (dropdown) {
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                }
            });
        }

        document.addEventListener('click', (event) => {
            if (!dropdownRoot.contains(event.target)) {
                const dropdown = dropdownRoot.querySelector('.dropdown');
                if (dropdown) {
                    dropdown.style.display = 'none';
                }
            }
        });
    }
}
